import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import { PageWrapper } from "../components/layout/container/Container";
import { PageHeader } from "../components/layout/header/Header";
import { PageHeading } from "../components/common/heading/Heading";
import BackButton from "../components/buttons/back/BackButton";

import "./Docs.css";

export default function GeneralPrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Katch | Privacy Policy";
  }, []);

  return (
    <PageWrapper>
      <br />
      <br />
      <PageHeader>
        <BackButton onClick={() => navigate(-1)} />

        <PageHeading
          title="Privacy Policy"
          subTitle="How Katch collects, uses, protects and manages your personal information."
        />
      </PageHeader>

      <section className="docs__section">
        <p>Last updated: November 22, 2025</p>

        <p>
          Welcome to <strong>Katch</strong> ("Katch", "we", "us", or "our"), a
          sports technology platform that enables users and sporting communities
          to stream, share, manage and monetise sports content.
        </p>

        <p>
          This Privacy Policy explains how we collect, use, store, disclose and
          protect personal information when you access Katch.one, its
          applications, features and related services (collectively, the
          "Service").
        </p>

        <p>
          We are committed to handling personal information responsibly and in
          accordance with applicable Australian privacy laws.
        </p>

        <h2>1. Information We Collect</h2>

        <h3>1.1. Account Information</h3>

        <p>
          When you register for or use a Katch account, we may collect
          information such as your name, username, email address, account
          credentials and other details you provide.
        </p>

        <p>
          We may also collect information relating to your account preferences,
          creator status, group memberships and content access permissions.
        </p>

        <h3>1.2. Profile and Talent ID Information</h3>

        <p>
          When you create or update a user profile or Talent ID, we may collect
          information such as your profile photograph, biography, sports,
          sporting experiences, achievements and other information you choose to
          provide.
        </p>

        <p>
          Depending on the features you use, this may include information about
          your sporting background, playing positions, assessment results and
          associated media.
        </p>

        <p>
          Some profile information may be visible to other users depending on
          your sharing settings and the features you use.
        </p>

        <h3>1.3. Uploaded Content</h3>

        <p>
          Katch allows users and creators to upload, publish and manage content,
          including videos, Pops, photo albums, live streams and associated
          descriptions.
        </p>

        <p>
          We may collect and process uploaded content, related metadata,
          thumbnails, timestamps, visibility settings and information necessary
          to store, display and deliver that content.
        </p>

        <p>
          Content may include images, video or audio of identifiable
          individuals. Users and creators are responsible for obtaining
          permissions and consent where required.
        </p>

        <h3>1.4. Usage and Activity Information</h3>

        <p>
          We may collect information about how you interact with Katch,
          including content you view, playback activity, saved Pops, searches,
          group participation and other interactions with the Service.
        </p>

        <p>
          This may also include your IP address, browser type, device
          information, operating system, access times and diagnostic
          information.
        </p>

        <h3>1.5. Payment and Transaction Information</h3>

        <p>
          When you purchase access to paid content, we may collect or receive
          transaction information, including purchase amounts, payment status,
          content purchased, transaction identifiers and access periods.
        </p>

        <p>
          Payments are processed through supported third-party payment
          providers, including Stripe. Payment information is handled according
          to the relevant provider's privacy and security practices.
        </p>

        <p>
          Katch may retain transaction records necessary to manage purchases,
          provide content access, handle disputes and meet legal obligations.
        </p>

        <h3>1.6. Cookies and Similar Technologies</h3>

        <p>
          We may use cookies and similar technologies to maintain user sessions,
          support authentication, remember preferences, protect content access
          and improve the Service.
        </p>

        <p>
          Some cookies are necessary for core platform functionality. Other
          technologies may be used for analytics or service improvements where
          implemented.
        </p>

        <p>
          You can manage or restrict cookies through your browser settings,
          although disabling certain cookies may affect your ability to use
          Katch.
        </p>

        <h2>2. How We Use Your Information</h2>

        <p>We may use personal information for the following purposes:</p>

        <ul>
          <li>To create, authenticate and manage user accounts.</li>

          <li>
            To provide video playback, live streaming, replays, Pops and photo
            albums.
          </li>

          <li>
            To enable creator uploads, content management and sharing features.
          </li>

          <li>
            To operate groups, memberships, access permissions and Talent ID
            profiles.
          </li>

          <li>To process purchases and manage paid content access.</li>

          <li>To provide customer support and respond to enquiries.</li>

          <li>
            To monitor performance, diagnose technical issues and improve Katch.
          </li>

          <li>
            To detect fraud, prevent misuse and maintain platform security.
          </li>

          <li>To comply with applicable laws and legal obligations.</li>
        </ul>

        <h2>3. Content Visibility and Sharing</h2>

        <h3>3.1. Public Content</h3>

        <p>
          Content published with public visibility may be accessible to other
          users through creator channels, search results, feeds or other areas
          of Katch.
        </p>

        <h3>3.2. Unlisted Content</h3>

        <p>
          Unlisted content is generally intended for access through a direct
          link or another authorised access method. It may still be viewed by
          people who receive the relevant link or permissions.
        </p>

        <h3>3.3. Group Content</h3>

        <p>
          Content restricted to groups may be accessible to users who have been
          granted the relevant group membership or content access permissions.
        </p>

        <p>
          Group creators and administrators may be able to manage membership
          requests and view information relevant to administering their groups.
        </p>

        <h3>3.4. Private Content</h3>

        <p>
          Private content is intended to be accessible only according to the
          permissions established by the platform and content owner.
        </p>

        <p>
          Katch may still process restricted content where necessary to operate
          the Service, provide support, investigate misuse or comply with law.
        </p>

        <h2>4. Live Streaming and Recorded Content</h2>

        <p>
          Katch provides tools that allow creators to broadcast live sports
          matches, events and other activities.
        </p>

        <p>
          Live streams may be transmitted, processed and delivered using
          third-party streaming infrastructure.
        </p>

        <p>
          When recording is enabled, broadcasts may be stored and processed into
          replays for subsequent viewing, subject to the relevant permissions.
        </p>

        <p>
          Creators are responsible for ensuring they have appropriate permission
          to record, publish and distribute events and identifiable individuals.
        </p>

        <h2>5. How We Share Information</h2>

        <p>
          We may disclose personal information where necessary to provide and
          operate the Service, including in the following circumstances.
        </p>

        <h3>5.1. Service Providers</h3>

        <p>
          We may use third-party providers for web hosting, cloud storage, video
          processing, content delivery, payment processing and related
          infrastructure.
        </p>

        <p>
          These may include providers such as Amazon Web Services and Stripe,
          depending on the services involved.
        </p>

        <h3>5.2. Other Users</h3>

        <p>
          Profile information, published content, creator channels and
          group-related information may be visible to other users according to
          the applicable access permissions.
        </p>

        <h3>5.3. Legal Requirements</h3>

        <p>
          We may disclose information where required or permitted by law,
          including in response to valid legal requests or to investigate
          unlawful activity.
        </p>

        <h3>5.4. Business Operations</h3>

        <p>
          Information may be disclosed where reasonably necessary for legitimate
          business operations, subject to applicable privacy laws and
          appropriate safeguards.
        </p>

        <h2>6. Data Storage and Security</h2>

        <p>
          Katch uses technical and organisational measures designed to protect
          personal information against unauthorised access, misuse, loss,
          alteration or disclosure.
        </p>

        <p>
          These measures may include access controls, authentication, protected
          storage, secure network connections and content delivery restrictions.
        </p>

        <p>
          However, no internet-based system or electronic storage method can
          guarantee absolute security.
        </p>

        <p>
          Users are responsible for protecting their account credentials and
          reporting suspected unauthorised access.
        </p>

        <h2>7. Overseas Processing and Storage</h2>

        <p>
          Katch may use service providers or infrastructure located outside
          Australia to host, process, store or deliver information.
        </p>

        <p>
          This may include cloud infrastructure used for video storage,
          streaming, content delivery and payment processing.
        </p>

        <p>
          Where applicable, we will take reasonable steps required by law in
          connection with the disclosure of personal information to overseas
          recipients.
        </p>

        <h2>8. Data Retention</h2>

        <p>
          We retain personal information for as long as reasonably necessary to
          provide the Service, manage accounts, process transactions, resolve
          disputes, meet legal obligations and maintain security.
        </p>

        <p>
          Uploaded content may remain stored while it is available through Katch
          or while retention is otherwise necessary for lawful purposes.
        </p>

        <p>
          When information is no longer required, we will take reasonable steps
          to delete or de-identify it where required by applicable law.
        </p>

        <h2>9. Your Privacy Choices and Rights</h2>

        <h3>9.1. Account Information</h3>

        <p>
          You may be able to review and update certain personal information
          through your account settings.
        </p>

        <h3>9.2. Content Management</h3>

        <p>
          Creators and users may manage certain uploaded content and visibility
          settings through the available platform tools.
        </p>

        <h3>9.3. Access and Correction</h3>

        <p>
          You may request access to or correction of personal information held
          about you, subject to applicable laws.
        </p>

        <h3>9.4. Account and Data Requests</h3>

        <p>
          You may contact Katch to request assistance with your account or
          enquire about the removal of personal information.
        </p>

        <p>
          Some information may need to be retained for legal, transaction,
          security or other legitimate purposes.
        </p>

        <h3>9.5. Marketing Communications</h3>

        <p>
          Where Katch sends promotional communications, you may opt out using
          any unsubscribe mechanism provided or by contacting support.
        </p>

        <h2>10. Children's Privacy</h2>

        <p>
          Katch is not intended for users under 13 years of age to register
          independently.
        </p>

        <p>
          Users under 18 may require parental or guardian consent, depending on
          applicable laws and the services being used.
        </p>

        <p>
          Katch may host sporting content featuring children and young people,
          including match recordings, photographs and athlete profiles.
        </p>

        <p>
          Creators must obtain any necessary permissions and consent before
          uploading or publishing content featuring children.
        </p>

        <p>
          Parents, guardians or other authorised persons who have concerns about
          content featuring a child may contact Katch to request a review.
        </p>

        <h2>11. Third-Party Services</h2>

        <p>
          Katch may integrate with third-party technologies, payment providers
          and infrastructure services.
        </p>

        <p>
          These providers may process information in accordance with their own
          privacy policies and applicable laws.
        </p>

        <p>
          We encourage users to review the relevant third-party privacy policies
          where appropriate.
        </p>

        <h2>12. Changes to This Privacy Policy</h2>

        <p>
          We may update this Privacy Policy to reflect changes to Katch, our
          operations, technology or applicable laws.
        </p>

        <p>
          Updated versions will be published on Katch with a revised
          last-updated date.
        </p>

        <p>
          Where required by law, we will provide appropriate notice of material
          changes.
        </p>

        <h2>13. Privacy Enquiries and Complaints</h2>

        <p>
          If you have questions about this Privacy Policy, wish to access or
          correct your information, or have a privacy complaint, please contact
          Katch.
        </p>

        <p>
          We will review privacy enquiries and complaints and respond in
          accordance with applicable legal requirements.
        </p>

        <p>
          If you are dissatisfied with our handling of a privacy complaint, you
          may have the right to contact the Office of the Australian Information
          Commissioner (OAIC).
        </p>

        <h2>14. Contact Us</h2>

        <p>
          For privacy enquiries, account concerns or content removal requests,
          please visit our <Link to="/support">Support Page</Link> or contact us
          at <a href="mailto:support@katch.one">support@katch.one</a>.
        </p>
      </section>
    </PageWrapper>
  );
}
